import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { IdentificationBadge, UploadSimple, Check, X, DownloadSimple, Sparkle, EnvelopeSimple } from "@phosphor-icons/react";
import { api, fileUrl, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

const daysLeft = (exp) => (exp ? Math.ceil((new Date(exp + "T00:00:00") - new Date()) / 86400000) : null);

function ExpiryBadge({ exp }) {
  const d = daysLeft(exp);
  if (d === null) return <Badge className="bg-slate-100 text-slate-600">no expiration on file</Badge>;
  if (d < 0) return <Badge className="bg-red-100 text-red-700">EXPIRED {Math.abs(d)}d ago</Badge>;
  if (d <= 10) return <Badge className="bg-red-100 text-red-700">{d}d left</Badge>;
  if (d <= 30) return <Badge className="bg-amber-100 text-amber-800">{d}d left</Badge>;
  if (d <= 90) return <Badge className="bg-yellow-100 text-yellow-800">{d}d left</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-800">{d}d left</Badge>;
}

export default function LicensesPage() {
  const { user, hasPerm, refresh } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [states, setStates] = useState([]);
  const [uploadState, setUploadState] = useState("");
  const [filters, setFilters] = useState({ user_id: "all", state: "all", status: "all" });
  const [preview, setPreview] = useState(null);
  const [reviewDates, setReviewDates] = useState({});
  const [attention, setAttention] = useState(null);
  const [remindBusy, setRemindBusy] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const isReviewer = hasPerm("licenses.review") || settings?.licensing_reviewer_id === user.id;

  const load = () => {
    const params = {};
    if (filters.user_id !== "all") params.user_id = filters.user_id;
    if (filters.state !== "all") params.state = filters.state;
    if (filters.status !== "all") params.status = filters.status;
    api.get("/licenses", { params }).then((r) => setLicenses(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/users").then((r) => setMembers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [filters]);
  useEffect(() => {
    if (isReviewer) api.get("/licenses/attention").then((r) => setAttention(r.data)).catch(() => {});
  }, [isReviewer]);

  const sendReminder = async (m) => {
    setRemindBusy(`${m.user_id}-${m.state}`);
    try {
      const { data } = await api.post("/licenses/remind", { user_id: m.user_id, state: m.state });
      toast.success(`Reminder emailed to ${data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reminder");
    } finally {
      setRemindBusy("");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadState) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("state", uploadState);
    fd.append("file", file);
    try {
      const { data } = await api.post("/licenses", fd);
      toast.success(data.expiration_date
        ? `License uploaded — AI detected expiration ${fmtDate(data.expiration_date)}. Awaiting admin review.`
        : "License uploaded — AI couldn't read the expiration; the reviewer will enter it manually.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const review = async (lic, status) => {
    try {
      await api.put(`/licenses/${lic.id}/review`, {
        status, comment: "",
        expiration_date: reviewDates[lic.id] || lic.expiration_date || null,
      });
      toast.success(`License ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    }
  };

  const assignReviewer = async (id) => {
    try {
      const { data } = await api.put("/settings", { licensing_reviewer_id: id });
      setSettings(data);
      toast.success("Licensing reviewer assigned");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not assign reviewer");
    }
  };

  const myStates = user.licensed_states || [];
  const currentByState = {};
  licenses.filter((l) => l.user_id === user.id && l.is_current).forEach((l) => { currentByState[l.state] = l; });
  const pendingQueue = licenses.filter((l) => l.status === "pending" && l.is_current);

  return (
    <div className="space-y-8">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Compliance</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
          <IdentificationBadge size={30} weight="duotone" className="text-indigo-600" /> State licenses
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Upload your insurance license for each state you're authorized to write in. AI reads the expiration date, the licensing admin reviews each upload, and you get renewal reminders 90/60/30 days out.
        </p>
      </div>

      {hasPerm("users.manage") && (
        <div className="bg-card border border-border rounded-md p-6 flex flex-wrap items-end gap-4" data-testid="licensing-reviewer-card">
          <div>
            <p className="text-sm font-semibold font-heading tracking-tight">Licensing reviewer</p>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">Responsible for reviewing licenses and receiving daily expiration alerts. Auto-reassigns to a super admin if deactivated.</p>
            <Select value={settings?.licensing_reviewer_id || ""} onValueChange={assignReviewer}>
              <SelectTrigger data-testid="licensing-reviewer-select" className="w-64"><SelectValue placeholder="Assign a reviewer" /></SelectTrigger>
              <SelectContent>
                {members.filter((m) => m.is_active).map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {myStates.length > 0 ? (
        <div className="bg-card border border-border rounded-md p-6 space-y-4" data-testid="my-licenses-card">
          <h3 className="font-heading font-semibold tracking-tight">My licensed states</h3>
          <div className="flex flex-wrap gap-2">
            {myStates.map((s) => {
              const lic = currentByState[s];
              return (
                <div key={s} data-testid={`my-state-${s}`} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
                  <span className="font-semibold">{s}</span>
                  {!lic && <Badge className="bg-red-100 text-red-700">license needed</Badge>}
                  {lic?.status === "rejected" && <Badge className="bg-red-100 text-red-700">rejected — re-upload needed</Badge>}
                  {lic && lic.status !== "rejected" && <Badge className={STATUS_STYLE[lic.status]}>{lic.status}</Badge>}
                  {lic?.expiration_date && lic.status !== "rejected" && <ExpiryBadge exp={lic.expiration_date} />}
                </div>
              );
            })}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div>
              <p className="text-sm font-medium mb-1.5">Upload / renew license</p>
              <Select value={uploadState} onValueChange={setUploadState}>
                <SelectTrigger data-testid="upload-state-select" className="w-48"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{myStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="license-file-input" />
            <Button data-testid="license-upload-btn" disabled={!uploadState || busy} onClick={() => fileRef.current?.click()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white h-10">
              <UploadSimple size={16} className="mr-2" /> {busy ? "Uploading & reading with AI…" : "Upload license"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Sparkle size={13} className="text-indigo-500" /> AI reads the expiration date automatically; the reviewer confirms it during approval.</p>
        </div>
      ) : (
        !isReviewer && <p className="text-sm text-muted-foreground bg-card border border-border rounded-md p-6" data-testid="no-states-msg">No licensed states assigned to you yet. Your admin will assign states you can write insurance in.</p>
      )}

      {isReviewer && attention && (
        <div className="bg-card border border-border rounded-md" data-testid="license-coverage-card">
          <div className="px-6 py-4 border-b border-border flex flex-wrap items-center gap-3">
            <h3 className="font-heading font-semibold tracking-tight">Team license coverage</h3>
            <Badge className={attention.missing.length ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-800"}>{attention.missing.length} missing</Badge>
            <Badge className={attention.expiring.length ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}>{attention.expiring.length} expiring ≤30d</Badge>
            <Badge className="bg-slate-100 text-slate-600">{attention.pending_review} pending review</Badge>
          </div>
          {attention.missing.length === 0 && attention.expiring.length === 0 ? (
            <p className="px-6 py-5 text-sm text-muted-foreground" data-testid="coverage-all-good">Every assigned state has a current license on file. Nothing needs chasing.</p>
          ) : (
            <div className="divide-y divide-border">
              {attention.missing.map((m) => (
                <div key={`${m.user_id}-${m.state}`} data-testid={`missing-license-${m.user_id}-${m.state}`} className="px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm"><strong>{m.user_name}</strong> — {m.state} <span className="text-xs text-red-600">({m.reason})</span></p>
                  {m.user_id !== user.id && (
                    <Button data-testid={`remind-btn-${m.user_id}-${m.state}`} size="sm" variant="outline" className="h-8"
                      disabled={remindBusy === `${m.user_id}-${m.state}`} onClick={() => sendReminder(m)}>
                      <EnvelopeSimple size={14} className="mr-1.5" /> {remindBusy === `${m.user_id}-${m.state}` ? "Sending…" : "Remind"}
                    </Button>
                  )}
                </div>
              ))}
              {attention.expiring.map((m) => (
                <div key={`exp-${m.user_id}-${m.state}`} className="px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm"><strong>{m.user_name}</strong> — {m.state} expires {fmtDate(m.expiration_date)}</p>
                  <ExpiryBadge exp={m.expiration_date} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isReviewer && pendingQueue.length > 0 && (
        <div className="bg-card border border-border rounded-md">
          <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">Pending review</h3></div>
          <div className="divide-y divide-border">
            {pendingQueue.map((l) => (
              <div key={l.id} data-testid={`license-review-row-${l.id}`} className="px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
                <button onClick={() => setPreview(l)} className="h-14 w-14 rounded-md border border-border overflow-hidden shrink-0 hover:opacity-80 transition-opacity">
                  {l.content_type?.startsWith("image/") ? <img src={fileUrl(l.file_path)} alt="license" className="h-full w-full object-cover" /> : <span className="text-xs flex h-full items-center justify-center bg-muted">PDF</span>}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{l.user_name} — {l.state}</p>
                  <p className="text-xs text-muted-foreground">
                    Uploaded {fmtDate(l.uploaded_at)} · {l.ai_detected ? `AI expiration: ${fmtDate(l.expiration_date)}` : "expiration not detected — enter below"}
                    {l.license_number && ` · #${l.license_number}`}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input data-testid={`license-exp-input-${l.id}`} type="date" className="w-40 h-9"
                    value={reviewDates[l.id] ?? (l.expiration_date || "")}
                    onChange={(e) => setReviewDates({ ...reviewDates, [l.id]: e.target.value })} />
                  <Button data-testid={`license-approve-btn-${l.id}`} size="sm" onClick={() => review(l, "approved")} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check size={15} className="mr-1" /> Approve</Button>
                  <Button data-testid={`license-reject-btn-${l.id}`} size="sm" variant="outline" onClick={() => review(l, "rejected")} className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"><X size={15} className="mr-1" /> Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-md">
        <div className="px-6 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-heading font-semibold tracking-tight">License registry</h3>
          <div className="flex flex-wrap gap-2">
            {isReviewer && (
              <Select value={filters.user_id} onValueChange={(v) => setFilters({ ...filters, user_id: v })}>
                <SelectTrigger data-testid="license-filter-user" className="w-44 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All members</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={filters.state} onValueChange={(v) => setFilters({ ...filters, state: v })}>
              <SelectTrigger data-testid="license-filter-state" className="w-28 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="license-filter-status" className="w-32 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="divide-y divide-border" data-testid="license-registry">
          {licenses.map((l) => (
            <div key={l.id} className="px-4 sm:px-6 py-3.5 flex flex-wrap items-center gap-3">
              <button onClick={() => setPreview(l)} className="h-10 w-10 rounded-md border border-border overflow-hidden shrink-0">
                {l.content_type?.startsWith("image/") ? <img src={fileUrl(l.file_path)} alt="license" className="h-full w-full object-cover" /> : <span className="text-[10px] flex h-full items-center justify-center bg-muted">PDF</span>}
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{l.user_name} — {l.state} {!l.is_current && <span className="text-xs text-muted-foreground">(superseded)</span>}</p>
                <p className="text-xs text-muted-foreground">Expires {l.expiration_date ? fmtDate(l.expiration_date) : "—"} · uploaded {fmtDate(l.uploaded_at)}{l.license_number && ` · #${l.license_number}`}</p>
              </div>
              <div className="flex items-center gap-2">
                {l.is_current && <ExpiryBadge exp={l.expiration_date} />}
                <Badge className={STATUS_STYLE[l.status]}>{l.status}</Badge>
                <Button data-testid={`license-download-btn-${l.id}`} size="sm" variant="outline" className="h-8"
                  onClick={() => window.open(`${fileUrl(l.file_path)}&download=1`, "_blank")}>
                  <DownloadSimple size={15} />
                </Button>
              </div>
            </div>
          ))}
          {licenses.length === 0 && <p className="px-6 py-10 text-sm text-muted-foreground">No licenses match these filters.</p>}
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="license-preview-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{preview?.user_name} — {preview?.state} license</DialogTitle></DialogHeader>
          {preview && (preview.content_type?.startsWith("image/")
            ? <img src={fileUrl(preview.file_path)} alt="License" className="w-full rounded-md border border-border" />
            : <Button onClick={() => window.open(`${fileUrl(preview.file_path)}&download=1`, "_blank")}>Download PDF</Button>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
